import { userStore } from '@stores/User';
import { Media } from '@typings/Media';
import { useEffect, useState } from 'react';

/**
 * Module-level promise cache for `(libraryId, mediaId) → Media`. Single-
 * flight: simultaneous callers for the same key share one in-flight
 * request, and resolved entries stay cached for the rest of the session.
 *
 * Failed fetches are evicted so a remount can retry — without that, a
 * transient 5xx would render the page useless until reload.
 */
const cache = new Map<string, Promise<Media>>();

function fetchMediaCached(libraryId: string, mediaId: string): Promise<Media> {
  const key = `${libraryId}:${mediaId}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = userStore
      .apiWithAuth<Media>(`/library/${libraryId}/media/${mediaId}`)
      .catch((err) => {
        cache.delete(key);
        throw err;
      });
    cache.set(key, pending);
  }
  return pending;
}

/**
 * Resolve a `Media` object by `(libraryId, mediaId)`. Returns `null`
 * while loading, when the IDs are missing, or if the fetch fails —
 * callers should always have a fallback (e.g. `instance.name`) since
 * this is a best-effort lookup.
 */
export function useMediaById(libraryId?: string, mediaId?: string): Media | null {
  const [media, setMedia] = useState<Media | null>(null);

  useEffect(() => {
    if (!libraryId || !mediaId) {
      setMedia(null);
      return;
    }
    let cancelled = false;
    fetchMediaCached(libraryId, mediaId)
      .then((m) => {
        if (!cancelled) setMedia(m);
      })
      .catch(() => {
        // Silent — caller has a fallback. Errors are logged by apiWithAuth.
      });
    return () => {
      cancelled = true;
    };
  }, [libraryId, mediaId]);

  return media;
}
